/**
 * Season Summary ACL gate helpers (map_season_access).
 * Season summary PDF Download follows map_season_access (canDownloadSeasonPdf).
 */

export const SEASON_ACCESS_LEVELS = Object.freeze([
  'hide',
  'metrics',
  'importers',
  'exporters',
  'all',
]);

/** Levels that show Season Summary chrome (title / empty shell / cards). */
const CHROME_LEVELS = new Set(['metrics', 'importers', 'exporters', 'all']);

/** Levels that may show variety cards from a non-null preview (`metrics` = locked teaser: names only). */
const VARIETY_CARD_LEVELS = new Set(['metrics', 'importers', 'exporters', 'all']);

/** Levels that may download the season summary PDF (explicit allowlist). The PDF carries both ranked sections, so only `all`. */
const SEASON_PDF_LEVELS = new Set(['all']);

export function normalizeSeasonAccess(raw) {
  const v = String(raw || '').trim().toLowerCase();
  return SEASON_ACCESS_LEVELS.includes(v) ? v : 'hide';
}

/** Show Season Summary section chrome (including metrics empty shell). */
export function showSeasonSummaryChrome(seasonAccess) {
  return CHROME_LEVELS.has(normalizeSeasonAccess(seasonAccess));
}

/** Use preview.varieties for the three header cards. */
export function showSeasonVarietyCards(seasonAccess, preview) {
  if (!VARIETY_CARD_LEVELS.has(normalizeSeasonAccess(seasonAccess))) return false;
  return preview != null && typeof preview === 'object';
}

/** A locked teaser pack (names only, numbers withheld by the backend). */
export function isLockedSeasonPack(preview) {
  return preview != null && typeof preview === 'object' && preview.locked === true;
}

/** Modal: render importers section only when the key was projected in. */
export function hasSeasonImportersSection(preview) {
  return preview != null && typeof preview === 'object' && Object.hasOwn(preview, 'importers');
}

/** Modal: render exporters section only when the key was projected in. */
export function hasSeasonExportersSection(preview) {
  return preview != null && typeof preview === 'object' && Object.hasOwn(preview, 'exporters');
}

/**
 * Resolve season_access from a preview API body.
 * Local fallback (DEV) without the field is treated as `all`.
 */
export function seasonAccessFromPreviewBody(body) {
  if (body && typeof body.season_access === 'string') {
    return normalizeSeasonAccess(body.season_access);
  }
  if (body?.local === true) return 'all';
  return 'hide';
}

/** Season summary PDF Download: unknown, loading or missing level denies. */
export function canDownloadSeasonPdf(level) {
  return typeof level === 'string' && SEASON_PDF_LEVELS.has(level.trim().toLowerCase());
}
